import React, { memo, forwardRef, useMemo } from 'react';
import cn from 'classnames';
import { useHover, usePress } from '@react-aria/interactions';
import { mergeProps } from '@react-aria/utils';
import { Box } from '../box';
import { SlotContext, useSlot } from '../slots';
import { useForwardedRef, usePatchedFocusRing } from '../../hooks';
import styles from './styles.module.css';
export const ListItemBase = memo(forwardRef(function ListItem({ children, size = 'm', hovered, pressed, focused, disabled, classes = {}, ...props }, ref) {
    const context = useMemo(() => ({ size }), [size]);
    const { heightClass, sizeClass } = useSlot({ size });
    return (React.createElement(SlotContext.Provider, { value: context },
        React.createElement(Box, { ref: ref, ...props, className: cn(styles.listItem, heightClass, sizeClass, props.className, hovered && classes.hovered, pressed && classes.pressed, focused && classes.focused, disabled && classes.disabled, {
                [styles.hovered]: hovered,
                [styles.pressed]: pressed,
                [styles.focused]: focused,
                [styles.disabled]: disabled,
            }) }, children)));
}));
export function useListItem(props, ref) {
    const { onPress, disabled, tabIndex, ...rest } = props;
    const elRef = useForwardedRef(ref);
    const { isFocusVisible, focusProps } = usePatchedFocusRing();
    const { hoverProps, isHovered } = useHover({ isDisabled: disabled });
    const { isPressed, pressProps } = usePress({ isDisabled: disabled, onPress });
    return {
        isPressed,
        isFocusVisible,
        isHovered,
        listItemProps: mergeProps(rest, hoverProps, focusProps, pressProps, {
            disabled,
            tabIndex: tabIndex !== null && tabIndex !== void 0 ? tabIndex : (disabled ? -1 : 0),
        }),
        elRef,
    };
}
export const ListItem = memo(forwardRef(function ListItem(props, ref) {
    const { isHovered, isFocusVisible, isPressed, listItemProps, elRef } = useListItem(props, ref);
    return (React.createElement(ListItemBase, { hovered: isHovered, pressed: isPressed, focused: isFocusVisible, ...listItemProps, ref: elRef }));
}));
